import axios, { getData } from "../../../axios";
import * as types from "./types";
import { createSlice } from "@reduxjs/toolkit";
import { createErrorReportingAsyncThunk } from "../../helpers";
import * as tPromise from "io-ts-promise";
import {
  getClientsResponseT,
  getFilterOptionsResponseT,
  createDeferredInvoiceT,
  createFolderReqT,
  getDeferredInvoicesResponseT,
  moveToFolderReqT,
  replaceClientReqT,
  updateFolderReqT,
} from "./types";

const prefix = "/data/consolidated-invoicing";

type State = {
  uninvoicedClientsInfo: types.UninvoicedClients;
  filterOptions: types.UninvoicedFilterOptions;
  data: null | {
    // The client the data was loaded for. Consumers must ignore data for
    // another client — a slow response can land after the selection changed.
    clientId: string;
    invoices: types.DeferredInvoice[];
    folders: types.DeferredInvoicesFolder[];
  };
};

const initialState: State = {
  uninvoicedClientsInfo: [],
  filterOptions: [],
  data: null,
};

export const loadUninvoicedClientsInfo = createErrorReportingAsyncThunk(
  `${prefix}/load-clients`,
  async (input?: {
    dateFrom?: string | null;
    dateTo?: string | null;
    cargoType?: string | null;
    truckId?: string | null;
    driverId?: string | null;
    search?: string | null;
  }) => {
    const params = new URLSearchParams();
    if (input?.dateFrom) {
      params.set("dateFrom", input.dateFrom);
    }
    if (input?.dateTo) {
      params.set("dateTo", input.dateTo);
    }
    if (input?.cargoType) {
      params.set("cargoType", input.cargoType);
    }
    if (input?.truckId) {
      params.set("truckId", input.truckId);
    }
    if (input?.driverId) {
      params.set("driverId", input.driverId);
    }
    if (input?.search) {
      params.set("search", input.search);
    }
    const query = params.toString();
    return axios
      .get(`/api/consolidated-invoicing/clients${query ? `?${query}` : ""}`)
      .then(getData)
      .then(tPromise.decode(getClientsResponseT));
  }
);

export const loadForClient = createErrorReportingAsyncThunk(
  `${prefix}/load-for-client`,
  async (clientId: string) => {
    const data = await axios
      .get(`/api/consolidated-invoicing/unsent-invoices?client=${clientId}`)
      .then(getData)
      .then(tPromise.decode(getDeferredInvoicesResponseT));
    return { data };
  }
);

// Org-wide (not scoped to a client) filter option rows for a date range, so
// the cargo type/truck/driver filter dropdowns work before a client is
// picked. Mirrors loadUninvoicedClientsInfo's date-param handling.
export const loadFilterOptions = createErrorReportingAsyncThunk(
  `${prefix}/load-filter-options`,
  async (input?: { dateFrom?: string | null; dateTo?: string | null }) => {
    const params = new URLSearchParams();
    if (input?.dateFrom) {
      params.set("dateFrom", input.dateFrom);
    }
    if (input?.dateTo) {
      params.set("dateTo", input.dateTo);
    }
    const query = params.toString();
    return axios
      .get(`/api/consolidated-invoicing/filter-options${query ? `?${query}` : ""}`)
      .then(getData)
      .then(tPromise.decode(getFilterOptionsResponseT));
  }
);

export const createInvoice = createErrorReportingAsyncThunk(
  `${prefix}/send-invoice`,
  async (input: {
    clientId: string;
    customerContactId: string | null;
    deliveryAddressId: string | null;
    invoiceRef: string;
    invoiceIds: string[];
    groupProducts: boolean;
    dueDate?: string;
  }) => {
    await axios.post(
      "/api/consolidated-invoicing/invoices",
      createDeferredInvoiceT.encode({
        ...input,
        dueDate: input.dueDate ?? null,
      })
    );
  }
);

export const moveDeferredInvoicesToFolder = createErrorReportingAsyncThunk(
  `${prefix}/move-invoices-to-folder`,
  async (
    input: {
      clientId: string;
      folderId: string | null;
      folderName: string | null;
      deferredInvoiceIds: string[];
    },
    thunkAPI
  ) => {
    const { dispatch } = thunkAPI;
    await axios.post(
      "/api/consolidated-invoicing/move-to-folder",
      moveToFolderReqT.encode(input)
    );
    await dispatch(loadForClient(input.clientId));
  }
);

// Replaces a client deleted in the finance system with another client: the
// server moves all of its uninvoiced lines and its folders over. Nothing of
// the deleted client is left to show, so its loaded data is emptied.
export const replaceClient = createErrorReportingAsyncThunk(
  `${prefix}/replace-client`,
  async (input: { fromClientId: string; toClientId: string }) => {
    await axios.post(
      "/api/consolidated-invoicing/replace-client",
      replaceClientReqT.encode(input)
    );
  }
);

export const renameFolder = createErrorReportingAsyncThunk(
  `${prefix}/rename-folder`,
  async (
    input: {
      folderId: string;
      name: string;
      clientId: string;
      deliveryAddressId: string | null;
    },
    thunkAPI
  ) => {
    const { dispatch } = thunkAPI;
    await axios.put(
      "/api/consolidated-invoicing/folders",
      updateFolderReqT.encode(input)
    );
    await dispatch(loadForClient(input.clientId));
  }
);

export const createEmptyFolder = createErrorReportingAsyncThunk(
  `${prefix}/create-empty-folder`,
  async (
    input: {
      clientId: string;
      folderName: string;
      deliveryAddressId: string | null;
    },
    thunkAPI
  ) => {
    const { dispatch } = thunkAPI;
    await axios.post(
      "/api/consolidated-invoicing/folders/create",
      createFolderReqT.encode(input)
    );
    await dispatch(loadForClient(input.clientId));
  }
);

export const deleteFolder = createErrorReportingAsyncThunk(
  `${prefix}/delete-folder`,
  async (
    input: {
      folderId: string;
      clientId: string;
    },
    thunkAPI
  ) => {
    const { dispatch } = thunkAPI;
    await axios.delete(`/api/consolidated-invoicing/folders/${input.folderId}`);
    await dispatch(loadForClient(input.clientId));
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    clearForClient: (state) => {
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadForClient.fulfilled, (state, action) => {
        state.data = { clientId: action.meta.arg, ...action.payload.data };
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        const invoicedInvoices = action.meta.arg.invoiceIds;
        if (state.data) {
          state.data.invoices = state.data.invoices.filter(
            (invoice) => !invoicedInvoices.includes(invoice.id)
          );
        }
      })
      .addCase(replaceClient.fulfilled, (state, action) => {
        if (state.data?.clientId === action.meta.arg.fromClientId) {
          state.data.invoices = [];
          state.data.folders = [];
        }
      })
      .addCase(loadUninvoicedClientsInfo.fulfilled, (state, action) => {
        state.uninvoicedClientsInfo = action.payload;
      })
      .addCase(loadFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      });
  },
});

export default slice.reducer;

export const { clearForClient } = slice.actions;
